/**
 * Composite component pairing ChampionPicker and LoadoutPicker (#1154).
 *
 * Provides a unified layout container for selecting a champion and customizing
 * their ability loadout while preserving individual picker contracts and data-testids.
 *
 * Supports two consumption patterns:
 * 1. Direct state binding: `<ChampionLoadoutPicker state={loadoutState} />`
 * 2. Explicit controlled props: `<ChampionLoadoutPicker championId={...} onChampionChange={...} loadout={...} onLoadoutChange={...} />`
 */

import type { CSSProperties, ReactElement } from "react";
import type { AbilityId, ChampionId } from "@pit/protocol";
import { ChampionPicker } from "./ChampionPicker.js";
import { LoadoutPicker } from "./LoadoutPicker.js";
import type { ChampionLoadoutState } from "./useChampionLoadoutState.js";

export interface ChampionLoadoutPickerControlledProps {
  readonly state?: never;
  readonly championId: ChampionId;
  readonly onChampionChange: (id: ChampionId) => void;
  readonly loadout: readonly AbilityId[];
  readonly onLoadoutChange: (loadout: readonly AbilityId[]) => void;
  readonly disabled?: boolean;
  readonly testId?: string;
  readonly layoutStyle?: CSSProperties;
}

export interface ChampionLoadoutPickerStateProps {
  readonly state: ChampionLoadoutState;
  readonly championId?: never;
  readonly onChampionChange?: never;
  readonly loadout?: never;
  readonly onLoadoutChange?: never;
  readonly disabled?: boolean;
  readonly testId?: string;
  readonly layoutStyle?: CSSProperties;
}

export type ChampionLoadoutPickerProps =
  | ChampionLoadoutPickerControlledProps
  | ChampionLoadoutPickerStateProps;

const DEFAULT_CONTAINER_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
  width: "100%",
};

const DEFAULT_CHAMPION_ROW_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
};

export function ChampionLoadoutPicker(props: ChampionLoadoutPickerProps): ReactElement {
  const { disabled = false, testId, layoutStyle } = props;

  const championId = props.state ? props.state.championId : props.championId;
  const onChampionChange = props.state ? props.state.setChampionId : props.onChampionChange;
  const loadout = props.state ? props.state.loadout : props.loadout;
  const onLoadoutChange = props.state ? props.state.setLoadout : props.onLoadoutChange;

  return (
    <div
      data-testid={testId}
      style={{ ...DEFAULT_CONTAINER_STYLE, ...layoutStyle }}
    >
      <div style={DEFAULT_CHAMPION_ROW_STYLE}>
        <ChampionPicker
          value={championId}
          onChange={onChampionChange}
          disabled={disabled}
        />
      </div>
      <LoadoutPicker
        championId={championId}
        value={loadout}
        onChange={onLoadoutChange}
        disabled={disabled}
      />
    </div>
  );
}
